import { useMemo, useState } from 'react';
import { Search, X, ChevronDown, ChevronUp, SlidersHorizontal } from 'lucide-react';
import {
  FILTERS,
  TOOLBAR_FILTER_KEYS,
  FilterDefinition,
  FilterContext,
  FilterOption,
  buildActiveFilterChips,
  getDeviceProductLine,
} from '../constants/filters';

export interface DashboardFiltersProps {
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClearAll: () => void;
  searchPlaceholder?: string;
  className?: string;
}

export function DashboardFilters({
  values,
  onChange,
  onClearAll,
  searchPlaceholder = 'Search by keywords, recall codes, device models, or authority...',
  className = '',
}: DashboardFiltersProps) {
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const chips = useMemo(() => buildActiveFilterChips(values), [values]);
  const searchValue = values.search ?? values.q ?? '';
  const sourceFilter = FILTERS.agency;
  const priorityFilter = FILTERS.urgency;
  const secondaryFilterKeys = TOOLBAR_FILTER_KEYS.filter(
    (key) => key !== sourceFilter.key && key !== priorityFilter.key
  );

  const handleFilterChange = (key: string, value: string) => {
    if (key === 'product') {
      onChange('product', value);
      if (value) {
        const owningLine = getDeviceProductLine(value);
        if (owningLine && owningLine !== values.productLine) {
          onChange('productLine', owningLine);
        }
      }
      return;
    }
    if (key === 'productLine') {
      onChange('productLine', value);
      if (value !== values.productLine) {
        const currentDevice = values.product;
        if (currentDevice) {
          const owningLine = getDeviceProductLine(currentDevice);
          if (owningLine && owningLine !== value) {
            onChange('product', '');
          }
        }
      }
      return;
    }
    onChange(key, value);
  };

  return (
    <div
      className={`bg-white rounded-xl border border-slate-200 shadow-xs ${className}`}
      data-testid="dashboard-filters"
    >
      {/* Keep the source filter visible while progressive disclosure contains the rest. */}
      <div className="flex flex-wrap items-center gap-2 p-3">
        <div className="relative min-w-[200px] flex-1 basis-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onChange('search', e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full text-xs bg-slate-50/80 border border-slate-200 rounded-lg px-3 py-2.5 pl-9 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:bg-white transition"
            aria-label="Search signals"
            data-testid="filter-search"
          />
          {searchValue && (
            <button
              type="button"
              onClick={() => onChange('search', '')}
              className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-700 text-xs cursor-pointer"
              aria-label="Clear search"
            >
              Clear
            </button>
          )}
        </div>
        <FilterSelect def={sourceFilter} ctx={{ values }} onChange={handleFilterChange} />
        <FilterSelect def={priorityFilter} ctx={{ values }} onChange={handleFilterChange} />
        <button
          type="button"
          onClick={() => setFiltersExpanded((expanded) => !expanded)}
          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-semibold transition cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-600 ${
            filtersExpanded ||
            chips.some((chip) => chip.key !== sourceFilter.key && chip.key !== priorityFilter.key)
              ? 'border-blue-200 bg-blue-50 text-blue-900'
              : 'border-slate-200 bg-slate-50/80 text-slate-700 hover:bg-white'
          }`}
          aria-expanded={filtersExpanded}
          aria-controls="dashboard-additional-filters"
        >
          <SlidersHorizontal className="w-3.5 h-3.5" />
          Filters
          {filtersExpanded ? (
            <ChevronUp className="w-3.5 h-3.5" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5" />
          )}
        </button>
      </div>

      {filtersExpanded && (
        <div
          id="dashboard-additional-filters"
          className="grid grid-cols-1 gap-2 border-t border-slate-100 bg-slate-50/40 p-3 sm:grid-cols-2 lg:grid-cols-3"
        >
          {secondaryFilterKeys.map((key) => {
            const def = FILTERS[key];
            if (!def) return null;
            return (
              <FilterSelect
                key={def.key}
                def={def}
                ctx={{ values }}
                onChange={handleFilterChange}
              />
            );
          })}
        </div>
      )}

      {/* Active Filter Chips Strip */}
      {chips.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-2 px-3 pb-3 pt-2 border-t border-slate-100"
          data-testid="active-filter-chips"
        >
          <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 font-medium mr-1">
            <SlidersHorizontal className="w-3 h-3" />
            Active Filters:
          </span>
          {chips.map((chip) => (
            <button
              type="button"
              key={chip.key}
              onClick={() => onChange(chip.key, '')}
              className={`group inline-flex items-center gap-1 px-2.5 py-0.5 rounded text-xs font-medium border transition cursor-pointer hover:shadow-xs ${chip.chipClass}`}
              aria-label={`Remove ${chip.label} filter: ${chip.displayValue}`}
            >
              {chip.displayValue}
              <X className="w-3 h-3 opacity-60 group-hover:opacity-100 transition" />
            </button>
          ))}
          <button
            type="button"
            onClick={onClearAll}
            className="ml-auto text-blue-700 hover:text-blue-900 font-semibold underline cursor-pointer text-xs"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
}

interface FilterSelectProps {
  def: FilterDefinition;
  ctx: FilterContext;
  onChange: (key: string, value: string) => void;
}

function FilterSelect({ def, ctx, onChange }: FilterSelectProps) {
  const value = ctx.values[def.key] ?? '';
  const options = useMemo(() => resolveOptions(def, ctx), [def, ctx]);

  return (
    <div className="relative min-w-[160px]">
      <select
        aria-label={def.label}
        value={value}
        onChange={(e) => onChange(def.key, e.target.value)}
        className={`w-full appearance-none text-xs rounded-lg pl-2.5 pr-8 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-600 transition cursor-pointer font-medium border ${
          value
            ? `${def.chipClass} font-semibold`
            : 'bg-slate-50/80 border-slate-200 text-slate-700 focus:bg-white'
        }`}
      >
        <option value="">{def.placeholder}</option>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
            {opt.hint ? ` — ${opt.hint}` : ''}
          </option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
    </div>
  );
}

const resolveOptions = (def: FilterDefinition, ctx: FilterContext): FilterOption[] => {
  if (typeof def.options === 'function') return def.options(ctx);
  return def.options;
};
